import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, formatRp } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { CheckCircle2, MapPin, Calendar, Package, Truck, Loader2, Plus, Trash2, Edit3, MessageCircle } from "lucide-react";

export default function OrderConfirm() {
  const { orderId } = useParams();
  const nav = useNavigate();
  const { settings } = useSettings();
  const [order, setOrder] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [items, setItems] = useState([]);
  const [done, setDone] = useState(false);
  const [invoice, setInvoice] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get(`/orders/${orderId}`),
      api.get("/products"),
    ]).then(([o, p]) => {
      setOrder(o.data);
      setItems(o.data.items.map(i => ({ product_id: i.product_id, quantity: i.quantity })));
      setProducts(p.data);
    }).catch(() => toast.error("Order tidak ditemukan"));
  }, [orderId]);

  const saveItems = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/orders/${orderId}/items`, { items });
      const fresh = await api.get(`/orders/${orderId}`);
      setOrder(fresh.data);
      setEditing(false);
      toast.success("Pesanan diperbarui");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal simpan");
    }
    setSaving(false);
  };

  const addItem = () => setItems([...items, { product_id: products[0]?.id, quantity: 1 }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const updateItem = (i, field, val) => {
    const copy = [...items];
    copy[i] = { ...copy[i], [field]: field === "quantity" ? Math.max(1, parseInt(val)||1) : val };
    setItems(copy);
  };

  const confirm = async () => {
    setLoading(true);
    try {
      const r = await api.post("/orders/confirm", { order_id: orderId });
      setInvoice(r.data.invoice_no);
      setDone(true);
      toast.success("Pesanan dikonfirmasi & diteruskan ke gudang");
    } catch (e) {
      toast.error("Gagal konfirmasi");
    }
    setLoading(false);
  };

  if (!order) return (
    <div className="tech-grid min-h-[calc(100vh-4rem)] flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-cyan-400 animate-spin"/>
    </div>
  );

  const editable = order.status === "pending_confirmation" && !done;
  const waNum = settings.wa_official_number || "6285147888865";

  return (
    <div className="tech-grid min-h-[calc(100vh-4rem)] py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="pill-cyan mb-3">KONFIRMASI PESANAN</div>
        <h1 className="font-display text-3xl font-bold text-white">Order <span className="text-cyan-400 font-mono-metric">{order.order_id}</span></h1>
        <p className="text-slate-400 text-sm mt-1">Periksa detail. Bila ada yang salah atau ingin tambah item, klik <strong className="text-cyan-300">Edit Pesanan</strong> — kalau sudah benar tinggal klik <strong className="text-emerald-300">Konfirmasi</strong>.</p>

        <Card className="glass glow-border bg-transparent rounded-2xl p-6 mt-6">
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div>
              <div className="text-[10px] font-mono-metric text-cyan-400 uppercase tracking-widest mb-1">MITRA</div>
              <div className="text-white font-semibold">{order.partner.name}</div>
              <div className="text-slate-400 text-sm">{order.partner.sppg_name}</div>
            </div>
            <div>
              <div className="text-[10px] font-mono-metric text-cyan-400 uppercase tracking-widest mb-1 flex items-center gap-1">
                <MapPin className="w-3 h-3"/> ALAMAT
              </div>
              <div className="text-slate-200 text-sm">{order.partner.address}</div>
            </div>
            <div>
              <div className="text-[10px] font-mono-metric text-cyan-400 uppercase tracking-widest mb-1 flex items-center gap-1">
                <Calendar className="w-3 h-3"/> JADWAL PENGIRIMAN
              </div>
              <div className="text-slate-200 text-sm">{new Date(order.scheduled_delivery).toLocaleString("id-ID")}</div>
            </div>
            <div>
              <div className="text-[10px] font-mono-metric text-cyan-400 uppercase tracking-widest mb-1">STATUS</div>
              <div className={`inline-block px-2 py-1 text-xs rounded-md font-mono-metric ${
                order.status === "confirmed" || done ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"
              }`}>{done ? "CONFIRMED" : order.status.toUpperCase()}</div>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] font-mono-metric text-cyan-400 uppercase tracking-widest flex items-center gap-1">
                <Package className="w-3 h-3"/> DETAIL PRODUK
              </div>
              {editable && !editing && (
                <Button data-testid="edit-order-btn" size="sm" variant="outline" onClick={() => setEditing(true)} className="h-8 border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 text-xs"><Edit3 className="w-3 h-3 mr-1"/>Edit Pesanan</Button>
              )}
            </div>

            {editing ? (
              <div className="space-y-2">
                {items.map((it, i) => {
                  const prod = products.find(p => p.id === it.product_id);
                  return (
                    <div key={i} data-testid={`edit-item-${i}`} className="grid grid-cols-12 gap-2 items-center bg-slate-900/40 border border-slate-800 rounded-lg p-3">
                      <div className="col-span-6">
                        <Select value={it.product_id} onValueChange={v => updateItem(i, "product_id", v)}>
                          <SelectTrigger className="bg-slate-800 border-slate-700 text-white h-9 text-xs"><SelectValue/></SelectTrigger>
                          <SelectContent className="bg-slate-900 border-slate-700 text-white">
                            {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name} · {formatRp(p.price)}/{p.unit}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <Input type="number" min="1" value={it.quantity} onChange={e => updateItem(i, "quantity", e.target.value)} className="col-span-3 bg-slate-800 border-slate-700 text-white h-9 text-xs"/>
                      <div className="col-span-2 text-xs text-cyan-300 font-mono-metric truncate">{prod ? formatRp(prod.price * it.quantity) : "-"}</div>
                      <Button data-testid={`del-item-${i}`} size="icon" variant="ghost" onClick={() => removeItem(i)} className="col-span-1 h-8 w-8 text-red-400"><Trash2 className="w-3 h-3"/></Button>
                    </div>
                  );
                })}
                <div className="flex gap-2 pt-2">
                  <Button data-testid="add-item-btn" size="sm" variant="outline" onClick={addItem} className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 text-xs"><Plus className="w-3 h-3 mr-1"/>Tambah Item</Button>
                  <Button data-testid="save-items-btn" size="sm" onClick={saveItems} disabled={saving} className="bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold text-xs">{saving ? "Menyimpan..." : "Simpan Perubahan"}</Button>
                  <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setItems(order.items.map(i => ({ product_id: i.product_id, quantity: i.quantity }))); }} className="text-slate-400 text-xs">Batal</Button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {order.items.map((it, i) => (
                  <div key={i} data-testid={`order-item-${i}`} className="flex justify-between items-center bg-slate-900/40 border border-slate-800 rounded-lg p-3">
                    <div>
                      <div className="text-white text-sm font-medium">{it.product_name}</div>
                      <div className="text-xs text-slate-400 font-mono-metric">{it.quantity} {it.unit} × {formatRp(it.price)}</div>
                    </div>
                    <div className="font-mono-metric text-cyan-300 font-bold">{formatRp(it.line_total)}</div>
                  </div>
                ))}
              </div>
            )}

            <div className="border-t border-slate-800 mt-4 pt-4 space-y-1 font-mono-metric">
              <div className="flex justify-between text-sm text-slate-300"><span>Subtotal</span><span>{formatRp(order.subtotal)}</span></div>
              <div className="flex justify-between text-sm text-slate-300"><span>PPh Final (0.5%)</span><span>{formatRp(order.tax)}</span></div>
              <div className="flex justify-between text-lg text-white font-bold mt-2"><span>TOTAL</span><span className="text-cyan-300">{formatRp(order.total)}</span></div>
            </div>
          </div>

          {!done ? (
            <div className="mt-6 space-y-3">
              <Button
                data-testid="confirm-order-btn"
                disabled={loading || editing}
                onClick={confirm}
                className="w-full h-12 bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold text-base shadow-[0_0_24px_rgba(0,240,255,0.35)]"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <><CheckCircle2 className="w-4 h-4 mr-2"/> KONFIRMASI PESANAN</>}
              </Button>
              <a href={`https://wa.me/${waNum}?text=${encodeURIComponent(`Halo, saya mau ubah pesanan ${orderId}`)}`} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 text-xs text-emerald-300 hover:text-emerald-200 font-mono-metric">
                <MessageCircle className="w-3 h-3"/>Butuh bantuan? Chat WA {settings.wa_official_display || "0851-4788-8865"}
              </a>
            </div>
          ) : (
            <div className="mt-6 space-y-3">
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-emerald-400 mt-0.5"/>
                <div>
                  <div className="font-semibold text-emerald-300">Pesanan diteruskan ke Gudang</div>
                  <div className="text-sm text-slate-300 mt-1">
                    Invoice <span className="font-mono-metric text-cyan-300">{invoice}</span> telah dibuat. AI Route Delivery menyusun rute pengiriman.
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button data-testid="view-dashboard-btn" onClick={() => nav("/dashboard")} className="flex-1 bg-slate-800 hover:bg-slate-700 text-white">
                  <Truck className="w-4 h-4 mr-2"/> Lihat Dashboard
                </Button>
                <Button data-testid="new-order-btn" onClick={() => nav("/chat")} variant="outline" className="flex-1 border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10">
                  Order Lagi
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
